import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, formatEUR } from "@/components/app/Shared";
import { PRODUCT_CATEGORIES } from "@/pages/Products";
import {
  Plus, Minus, ShoppingCart, Trash2, Search, ChevronLeft, ChevronRight,
  Carrot, Fish, CupSoda, UtensilsCrossed, Package, GlassWater, Wine,
  SprayCan, Coffee, Boxes,
} from "lucide-react";
import { toast } from "sonner";

const CATEGORY_META = {
  "Verse Groente":          { icon: Carrot,           color: "bg-emerald-50 text-emerald-600 border-emerald-200", accent: "from-emerald-500/15 to-emerald-500/0" },
  "Vlees, Vis & Diepvries": { icon: Fish,             color: "bg-rose-50 text-rose-600 border-rose-200",         accent: "from-rose-500/15 to-rose-500/0" },
  "Drinks":                 { icon: CupSoda,          color: "bg-sky-50 text-sky-600 border-sky-200",            accent: "from-sky-500/15 to-sky-500/0" },
  "Overige Food":           { icon: UtensilsCrossed,  color: "bg-amber-50 text-amber-600 border-amber-200",      accent: "from-amber-500/15 to-amber-500/0" },
  "Non-Food":               { icon: Package,          color: "bg-zinc-100 text-zinc-700 border-zinc-300",        accent: "from-zinc-400/15 to-zinc-400/0" },
  // Drinks subs
  "Soft Drinks":            { icon: CupSoda,          color: "bg-sky-50 text-sky-600 border-sky-200",            accent: "from-sky-500/15 to-sky-500/0" },
  "Water":                  { icon: GlassWater,       color: "bg-cyan-50 text-cyan-600 border-cyan-200",         accent: "from-cyan-500/15 to-cyan-500/0" },
  "Alcohol":                { icon: Wine,             color: "bg-purple-50 text-purple-600 border-purple-200",   accent: "from-purple-500/15 to-purple-500/0" },
  // Non-Food subs
  "Hygiëne":                { icon: SprayCan,         color: "bg-teal-50 text-teal-600 border-teal-200",         accent: "from-teal-500/15 to-teal-500/0" },
  "Take-Away":              { icon: Coffee,           color: "bg-orange-50 text-orange-600 border-orange-200",   accent: "from-orange-500/15 to-orange-500/0" },
  "Overig":                 { icon: Boxes,            color: "bg-zinc-100 text-zinc-700 border-zinc-300",        accent: "from-zinc-400/15 to-zinc-400/0" },
};

// Top-level groups with optional sub-categories
const FOOD_SUBS = ["Verse Groente", "Vlees, Vis & Diepvries", "Overige Food"];
const DRINK_SUBS = ["Soft Drinks", "Water", "Alcohol"];
const NONFOOD_SUBS = ["Hygiëne", "Take-Away", "Overig"];
const TOP_LEVEL = [
  { key: "Food",     label: "Food",     icon: UtensilsCrossed, color: "bg-amber-50 text-amber-600 border-amber-200", accent: "from-amber-500/15 to-amber-500/0", subs: FOOD_SUBS },
  { key: "Drinks",   label: "Drinks",   icon: CupSoda,         color: "bg-sky-50 text-sky-600 border-sky-200",      accent: "from-sky-500/15 to-sky-500/0",      subs: DRINK_SUBS },
  { key: "Non-Food", label: "Non-Food", icon: Package,         color: "bg-zinc-100 text-zinc-700 border-zinc-300",  accent: "from-zinc-400/15 to-zinc-400/0",    subs: NONFOOD_SUBS },
];

const CART_STORAGE_KEY = "stockly_cart_v1";
const CART_TTL_MS = 20 * 60 * 60 * 1000; // 20 hours

function loadStoredCart() {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (!parsed.expiresAt || parsed.expiresAt < Date.now()) {
      localStorage.removeItem(CART_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export default function NewOrder() {
  const { user, showPrices, showSuppliers } = useAuth();
  const nav = useNavigate();
  const stored = loadStoredCart();
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [restaurantId, setRestaurantId] = useState(stored?.restaurantId || "");
  const [supplierFilter, setSupplierFilter] = useState("alle");
  const [q, setQ] = useState("");
  const [group, setGroup] = useState(null); // null = show top-level (Food/Drinks/Non-Food)
  const [category, setCategory] = useState(null); // selected leaf category (product list)
  const [cart, setCart] = useState(stored?.cart || {});
  const [notes, setNotes] = useState(stored?.notes || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const [p, s, r] = await Promise.all([
        api.get("/products"),
        showSuppliers ? api.get("/suppliers") : Promise.resolve({ data: [] }),
        api.get("/restaurants"),
      ]);
      setProducts(p.data);
      setSuppliers(s.data);

      let available = r.data;
      if (user?.role === "chef") {
        const myIds = user.restaurant_ids?.length ? user.restaurant_ids : (user.restaurant_id ? [user.restaurant_id] : []);
        available = r.data.filter((x) => myIds.includes(x.id));
      }
      setRestaurants(available);
      // Only keep the selected restaurant if it's still available to this user; otherwise pick a sane default
      setRestaurantId((prev) => {
        if (prev && available.find((x) => x.id === prev)) return prev;
        if (user?.restaurant_id && available.find((x) => x.id === user.restaurant_id)) return user.restaurant_id;
        return available[0]?.id || "";
      });
    })();
  }, [showSuppliers, user]);

  // Persist cart + notes + restaurant for 20 hours across navigation
  useEffect(() => {
    try {
      if (Object.keys(cart).length === 0 && !notes) {
        localStorage.removeItem(CART_STORAGE_KEY);
        return;
      }
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify({
        cart, notes, restaurantId,
        expiresAt: Date.now() + CART_TTL_MS,
      }));
    } catch { /* ignore quota errors */ }
  }, [cart, notes, restaurantId]);

  const categoryCounts = useMemo(() => {
    const counts = {};
    for (const p of products) {
      // Count by subcategory when product is in a group that uses subcategories (Drinks/Non-Food)
      const usesSub = DRINK_SUBS.includes(p.subcategory) || NONFOOD_SUBS.includes(p.subcategory);
      const key = usesSub ? p.subcategory : (PRODUCT_CATEGORIES.includes(p.category) ? p.category : "Overige Food");
      counts[key] = (counts[key] || 0) + 1;
    }
    return counts;
  }, [products]);

  const groupCount = (g) => {
    if (g.subs) return g.subs.reduce((sum, s) => sum + (categoryCounts[s] || 0), 0);
    return categoryCounts[g.key] || 0;
  };

  const filteredProducts = useMemo(() => products.filter((p) => {
    if (category) {
      if (DRINK_SUBS.includes(category) || NONFOOD_SUBS.includes(category)) {
        if (p.subcategory !== category) return false;
      } else if (p.category !== category) {
        return false;
      }
    }
    const matchS = !showSuppliers || supplierFilter === "alle" || p.supplier_id === supplierFilter;
    const matchQ = !q || p.name.toLowerCase().includes(q.toLowerCase()) || (p.barcode || "").includes(q);
    return matchS && matchQ;
  }), [products, category, supplierFilter, q, showSuppliers]);

  const addToCart = (p) => setCart((c) => ({ ...c, [p.id]: (c[p.id] || 0) + 1 }));
  const decFromCart = (p) => setCart((c) => {
    const n = (c[p.id] || 0) - 1;
    const next = { ...c };
    if (n <= 0) delete next[p.id]; else next[p.id] = n;
    return next;
  });
  const removeFromCart = (pid) => setCart((c) => { const x = { ...c }; delete x[pid]; return x; });

  const cartItems = Object.entries(cart).map(([pid, qty]) => {
    const p = products.find((x) => x.id === pid);
    return { product: p, qty };
  }).filter((x) => x.product);

  const total = cartItems.reduce((s, it) => s + (it.product.price || 0) * it.qty, 0);

  const submit = async () => {
    if (!restaurantId) { toast.error("Kies een restaurant"); return; }
    if (cartItems.length === 0) { toast.error("Voeg minstens 1 product toe"); return; }
    setSaving(true);
    try {
      const items = cartItems.map((it) => ({ product_id: it.product.id, quantity: it.qty }));
      const { data } = await api.post("/orders", { restaurant_id: restaurantId, items, notes });
      toast.success("Bestelling geplaatst!");
      setCart({});
      setNotes("");
      try { localStorage.removeItem(CART_STORAGE_KEY); } catch { /* ignore */ }
      nav(`/bestellingen/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mislukt");
    } finally { setSaving(false); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto" data-testid="new-order-page">
      <PageHeader
        overline="Nieuwe bestelling"
        title={category ? category : group ? group : "Kies een categorie"}
        description={category ? "Selecteer producten of voeg een nieuw product toe." : group ? `Kies een subcategorie binnen ${group}.` : "Begin met het kiezen van een productcategorie."}
        actions={
          <Link to="/producten" className="inline-flex items-center gap-2 h-10 px-4 bg-[#1B2D5C] hover:bg-[#142244] text-white rounded-full text-sm font-semibold" data-testid="add-product-cta">
            <Plus size={16} /> Nieuw product
          </Link>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6">
        <div>
          {!category && !group && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="category-grid">
              {TOP_LEVEL.map((g) => {
                const Icon = g.icon;
                const count = groupCount(g);
                const sub = g.subs ? `${g.subs.length} subcategorieën · ${count} product${count === 1 ? "" : "en"}` : `${count} product${count === 1 ? "" : "en"}`;
                return (
                  <button
                    key={g.key}
                    onClick={() => g.subs ? setGroup(g.key) : setCategory(g.key)}
                    className="group relative card-flat overflow-hidden p-6 text-left hover:shadow-md transition-all hover:-translate-y-0.5"
                    data-testid={`category-tile-${g.key}`}
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${g.accent} opacity-60 pointer-events-none`} />
                    <div className="relative">
                      <div className={`w-12 h-12 rounded-lg border ${g.color} flex items-center justify-center mb-5`}>
                        <Icon size={22} strokeWidth={1.8} />
                      </div>
                      <div className="overline mb-1 text-[10px]">{sub}</div>
                      <div className="text-xl font-bold tracking-tight text-[#1B2D5C] mb-3">{g.label}</div>
                      <div className="inline-flex items-center gap-1 text-xs font-semibold text-[#5BC044] group-hover:gap-2 transition-all">
                        Bekijk producten <ChevronRight size={13} />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {!category && group && (() => {
            const subs = group === "Food" ? FOOD_SUBS : group === "Drinks" ? DRINK_SUBS : NONFOOD_SUBS;
            const subgridTestId = group === "Food" ? "food-subgrid" : group === "Drinks" ? "drinks-subgrid" : "nonfood-subgrid";
            return (
              <>
                <button
                  onClick={() => setGroup(null)}
                  className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-[#1B2D5C] mb-4"
                  data-testid="back-to-toplevel"
                >
                  <ChevronLeft size={15} /> Alle categorieën
                </button>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={subgridTestId}>
                  {subs.map((c) => {
                    const meta = CATEGORY_META[c];
                    const Icon = meta.icon;
                    const count = categoryCounts[c] || 0;
                    return (
                      <button
                        key={c}
                        onClick={() => setCategory(c)}
                        className="group relative card-flat overflow-hidden p-6 text-left hover:shadow-md transition-all hover:-translate-y-0.5"
                        data-testid={`category-tile-${c}`}
                      >
                        <div className={`absolute inset-0 bg-gradient-to-br ${meta.accent} opacity-60 pointer-events-none`} />
                        <div className="relative">
                          <div className={`w-12 h-12 rounded-lg border ${meta.color} flex items-center justify-center mb-5`}>
                            <Icon size={22} strokeWidth={1.8} />
                          </div>
                          <div className="overline mb-1 text-[10px]">{count} product{count === 1 ? "" : "en"}</div>
                          <div className="text-xl font-bold tracking-tight text-[#1B2D5C] mb-3">{c}</div>
                          <div className="inline-flex items-center gap-1 text-xs font-semibold text-[#5BC044] group-hover:gap-2 transition-all">
                            Bekijk producten <ChevronRight size={13} />
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </>
            );
          })()}

          {category && (
            <>
              <button
                onClick={() => {
                  setCategory(null);
                  setQ("");
                  setSupplierFilter("alle");
                  if (!FOOD_SUBS.includes(category) && !DRINK_SUBS.includes(category) && !NONFOOD_SUBS.includes(category)) setGroup(null);
                }}
                className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-[#1B2D5C] mb-4"
                data-testid="back-to-categories"
              >
                <ChevronLeft size={15} /> {FOOD_SUBS.includes(category) ? "Terug naar Food" : DRINK_SUBS.includes(category) ? "Terug naar Drinks" : NONFOOD_SUBS.includes(category) ? "Terug naar Non-Food" : "Alle categorieën"}
              </button>

              <div className="flex flex-wrap gap-2 mb-4">
                <div className="relative flex-1 min-w-[200px]">
                  <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Zoeken in ${category}…`}
                    className="w-full h-10 pl-9 pr-3 border border-zinc-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#5BC044]/45 focus:border-[#5BC044] bg-white"
                    data-testid="catalog-search"
                  />
                </div>
                {showSuppliers && (
                  <select value={supplierFilter} onChange={(e) => setSupplierFilter(e.target.value)}
                    className="h-10 px-3 border border-zinc-300 rounded-full text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#5BC044]/45"
                    data-testid="supplier-filter"
                  >
                    <option value="alle">Alle leveranciers</option>
                    {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                )}
              </div>

              {filteredProducts.length === 0 ? (
                <div className="py-12 text-center text-sm text-zinc-500 border border-dashed border-zinc-200 rounded-md" data-testid="no-products">
                  Geen producten gevonden in deze categorie.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="product-grid">
                  {filteredProducts.map((p) => {
                    const supplier = showSuppliers ? suppliers.find((s) => s.id === p.supplier_id) : null;
                    const qty = cart[p.id] || 0;
                    return (
                      <div key={p.id} className="card-flat overflow-hidden flex flex-col" data-testid={`product-card-${p.id}`}>
                        {p.image_url && (
                          <div className="aspect-[16/10] bg-zinc-100 bg-cover bg-center" style={{ backgroundImage: `url(${p.image_url})` }} />
                        )}
                        <div className="p-4 flex flex-col flex-1">
                          {showSuppliers && supplier && <div className="overline mb-1 text-[10px]">{supplier.name}</div>}
                          <div className="font-semibold text-[15px] tracking-tight mb-1">{p.name}</div>
                          <div className="text-xs text-zinc-500 mb-3">per {p.unit}{p.category ? ` • ${p.category}` : ""}</div>
                          <div className="flex items-center justify-between mt-auto">
                            {showPrices ? (
                              <div className="font-mono font-bold text-[#1B2D5C]">{formatEUR(p.price)}</div>
                            ) : (
                              <div />
                            )}
                            {qty === 0 ? (
                              <button onClick={() => addToCart(p)} className="h-8 px-3 bg-[#1B2D5C] hover:bg-[#142244] text-white rounded text-xs font-semibold inline-flex items-center gap-1.5" data-testid={`add-product-${p.id}`}>
                                <Plus size={13} /> Toevoegen
                              </button>
                            ) : (
                              <div className="flex items-center gap-1 bg-zinc-100 rounded p-0.5">
                                <button onClick={() => decFromCart(p)} className="w-7 h-7 rounded hover:bg-white" data-testid={`dec-${p.id}`}><Minus size={13} className="mx-auto" /></button>
                                <span className="px-2 font-mono font-semibold text-sm w-6 text-center">{qty}</span>
                                <button onClick={() => addToCart(p)} className="w-7 h-7 rounded hover:bg-white" data-testid={`inc-${p.id}`}><Plus size={13} className="mx-auto" /></button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        <aside className="card-flat p-6 h-fit lg:sticky lg:top-8" data-testid="cart-panel">
          <div className="flex items-center gap-2 mb-4">
            <ShoppingCart size={18} />
            <h3 className="text-lg font-bold tracking-tight text-[#1B2D5C]">Winkelmandje</h3>
            <span className="ml-auto text-xs font-mono text-zinc-500">{cartItems.length} item{cartItems.length === 1 ? "" : "s"}</span>
          </div>

          {restaurants.length > 1 ? (
            <div className="mb-4">
              <label className="overline block mb-2">Restaurant</label>
              <select value={restaurantId} onChange={(e) => setRestaurantId(e.target.value)}
                className="w-full h-10 px-3 border border-zinc-300 rounded-full text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#5BC044]/45"
                data-testid="cart-restaurant-select"
              >
                <option value="">— Kies —</option>
                {restaurants.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
          ) : restaurants.length === 1 ? (
            <div className="mb-4">
              <label className="overline block mb-2">Restaurant</label>
              <div className="h-10 px-3 flex items-center bg-zinc-50 border border-zinc-200 rounded-full text-sm font-medium text-zinc-700" data-testid="cart-restaurant-fixed">
                {restaurants[0].name}
              </div>
            </div>
          ) : null}

          <div className="space-y-2 mb-4 max-h-[300px] overflow-y-auto">
            {cartItems.length === 0 ? (
              <div className="text-sm text-zinc-400 py-6 text-center border border-dashed border-zinc-200 rounded-md">
                Nog leeg. Kies een categorie en voeg producten toe.
              </div>
            ) : cartItems.map((it) => (
              <div key={it.product.id} className="flex items-center gap-2 text-sm py-2 border-b border-zinc-100" data-testid={`cart-item-${it.product.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{it.product.name}</div>
                  <div className="text-xs text-zinc-500 font-mono">
                    {it.qty} × {it.product.unit}{showPrices ? ` • ${formatEUR(it.product.price)}` : ""}
                  </div>
                </div>
                {showPrices && <div className="font-mono font-semibold">{formatEUR(it.qty * it.product.price)}</div>}
                <button onClick={() => removeFromCart(it.product.id)} className="text-zinc-400 hover:text-red-600 p-1" data-testid={`remove-${it.product.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>

          <div>
            <label className="overline block mb-2">Notities</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              placeholder="Bijv. leveringstijd…"
              className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#5BC044]/45"
              data-testid="cart-notes"
            />
          </div>

          {showPrices && (
            <div className="border-t border-zinc-200 mt-4 pt-4 flex items-center justify-between">
              <span className="overline">Totaal</span>
              <span className="font-mono text-xl font-bold text-[#1B2D5C]" data-testid="cart-total">{formatEUR(total)}</span>
            </div>
          )}

          <button onClick={submit} disabled={saving || cartItems.length === 0}
            className="mt-4 w-full h-11 bg-[#5BC044] hover:bg-[#4FAA3A] disabled:opacity-50 text-white rounded-full font-semibold"
            data-testid="submit-order-button"
          >
            {saving ? "Bezig…" : "Bestelling plaatsen"}
          </button>
        </aside>
      </div>
    </div>
  );
}
